import {
  SelectorNumberFn,
  SelectorStringFn,
  SortDirection,
} from '@epicuro-next/models/sorting';

export function compareStrings<T>(selector: SelectorStringFn<T>) {
  return (a: T, b: T, sortDirection: SortDirection) => {
    if (sortDirection === SortDirection.Asc) {
      return selector(a).localeCompare(selector(b));
    }

    return selector(b).localeCompare(selector(a));
  };
}

export function compareNumbers<T>(selector: SelectorNumberFn<T>) {
  return (a: T, b: T, sortDirection: SortDirection) => {
    if (sortDirection === SortDirection.Asc) {
      return selector(a) - selector(b);
    }

    return selector(b) - selector(a);
  };
}

export function compareDates<T>(selector: SelectorStringFn<T>) {
  return (a: T, b: T, sortDirection: SortDirection) => {
    const dateA = new Date(selector(a));
    const dateB = new Date(selector(b));
    if (sortDirection === SortDirection.Asc) {
      return dateA.getTime() - dateB.getTime();
    }

    return dateB.getTime() - dateA.getTime();
  };
}

/**
 * Compares items based on a custom order of values.
 * @param selector Function to extract the value to compare
 * @param valuesOrder Array defining the custom sort order
 * @returns A comparison function that sorts according to the specified order
 */
// tslint:disable-next-line:cognitive-complexity
export function compareCustomOrder<T, V extends string>(
  selector: (item: T) => V | undefined | null,
  valuesOrder: V[],
) {
  return (a: T, b: T, sortDirection: SortDirection = SortDirection.Asc) => {
    const valueA = selector(a) ?? '';
    const valueB = selector(b) ?? '';

    const indexA = valuesOrder.indexOf(valueA as V);
    const indexB = valuesOrder.indexOf(valueB as V);

    // If both values are in valuesOrder, sort by their position
    if (indexA !== -1 && indexB !== -1) {
      return sortDirection === SortDirection.Asc
        ? indexA - indexB
        : indexB - indexA;
    }

    // If only one value is in valuesOrder, prioritize it
    // Always place values in valuesOrder above those not in the list
    if (indexA !== -1) return -1; // Value A is in the list, so it comes first
    if (indexB !== -1) return 1; // Value B is in the list, so it comes first

    // If neither value is in valuesOrder, maintain original order
    return 0;
  };
}
